import { useAtomValue } from 'jotai';
import type { AuthSessionId, UserId, UnixTimestampMilliSeconds } from 'balus-common';
import { authStatusAtom } from './authStateAtom';

export type AuthStatus = {
  /**
   * 認証状態の判別子。
   * - 'authenticated': Balus にログイン済み(currentUserId/sessionId あり)
   * - 'unauthenticated': 未ログイン
   * - 'pendingRegistration': Firebase 認証済みだが Balus ユーザー未作成(登録前)
   */
  status: 'authenticated' | 'unauthenticated' | 'pendingRegistration';
  currentUserId: UserId | null;
  currentSessionId: AuthSessionId | null;
  userType: 'regular' | 'anonymous' | null;
  signInAt: UnixTimestampMilliSeconds | null;
};

/**
 * 認証状態(AuthStatus)を取り出すカスタムフック。
 * 認証状態は Jotai の authStatusAtom を参照する。未ログイン時は atom の既定値
 * (未ログイン状態)を返すため、例外は送出しない。
 */
export const useAuthStatus = (): AuthStatus => {
  return useAtomValue(authStatusAtom);
};

/**
 * 現在ログイン中のユーザIDを返します。未ログイン時にはnullを返します。
 */
export const useCurrentUserId = (): UserId | null => {
  const auth = useAuthStatus();
  return auth.currentUserId || null;
};

/**
 * 現在ログイン中のユーザIDを返します。未ログイン時には例外を返します。
 */
export const useRequiredCurrentUserId = (): UserId => {
  const userId = useCurrentUserId();
  if (!userId) throw new Error('Current user is not found');
  return userId;
};

export const useUserType = (): AuthStatus['userType'] => {
  const auth = useAuthStatus();
  return auth.userType;
};

export const useCurrentSessionId = (): AuthSessionId | null => {
  const auth = useAuthStatus();
  return auth.currentSessionId || null;
};
